/* Example Usage

    ---------------------------------------------

    First, enhance the data with the props required for SimpleTable:
    **Important**: Add actionsDisabled object to the item.

    let enhancedData = [];
    enhancedData = this.state.workshops.map((arrayItem) => {
        let item = JSON.parse(JSON.stringify(arrayItem))

        // Add any calculated parameters
        item.date = moment(item.fromDate).format('YYYY-MM-DD');

        // Decides whether an action is disabled.
        item.actionsDisabled = {
            view: false,
            edit: false,
            cancel: false
        }

        return item;
    });

    const tableCells = [
        { id: 'ID', label: 'Class ID', centered: false },
        { id: 'date', label: 'Date', centered: false },
        { id: 'location', label: 'Location', centered: false },
        { id: 'status', label: 'Status', centered: false }
    ];

    const actionButtons = [
        { icon: "view", action: this.onView, delete: false },
        { icon: "edit", action: this.onEdit, delete: false },
        { icon: "cancel", action: this.onCancel, delete: false }
    ];

    ---------------------------------------------

    Usage:
    
    <SimpleTable
        data={enhancedData}
        cellKey={"ID"}
        tableCells={tableCells}
        actionButtons={actionButtons}
        actions={true}
    />

*   Parameters/Props:
        data:  array [required]
        cellKey:  string [required]
        tableCells: array (of cell objects) [required]
                    cell: {
                        id: string [required]
                        label: string [required]
                        centered: boolean [required]
                    }
        actionButtons:  array (of button objects) [optional]
                        button: {
                            icon: string [required]
                            action: function [required]
                            delete: boolean [required] (whether this is the delete action button)
                        }
        actions: boolean [required] (whether you want to render action buttons like delete, edit, etc.)
*/

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import WavesIcon from '@mui/icons-material/Waves';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import RemoveIcon from '@mui/icons-material/Remove';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import Grid from '@mui/material/Grid';
import SimpleTableHead from '../helper/SimpleTableHead';
import TurnedInIcon from '@mui/icons-material/TurnedIn';

const useStyles = makeStyles({
    root: {
        '& .MuiTextField-root': {
            minWidth: '25ch',
        },
        '& .MuiTableCell-head': {
            fontWeight: 'bold'
        }
    },
    deleteButton: {
        color: '#d90000'
    }
});

// gets icon from material ui for each case
function getIcon(iconName) {
    switch (iconName) {
        case 'edit':
            return (<EditIcon />); //pencil
        case 'delete':
            return (<DeleteIcon />); //trash can
        case 'view':
            return (<VisibilityIcon />); //eye
        case 'cancel':
            return (<BlockIcon />); // circle with diagnoal line through it
        case 'register':
            return (<EventSeatIcon />); // an armchair
        case 'makeDefaultContact':
            return (<ContactPhoneIcon />);
        case 'waiver':
            return <WavesIcon /> // 3 wavy lines
        case 'accept':
            return <CheckCircleIcon /> //circle with check
        case 'lock':
            return <LockOpenIcon /> //partailly open lock
        case 'profile':
            return <PersonIcon /> //person head
        case 'add':
            return <AddCircleOutlineIcon /> // + in circle
        case 'remove':
            return <RemoveCircleIcon /> // - in circle
        case 'up':
            return <KeyboardArrowUpIcon /> //up arrow
        case 'down':
            return <KeyboardArrowDownIcon /> //down arrow
        case 'upDown':
            return <ImportExportIcon /> //up down arrow
        case 'play':
            return <PlayCircleOutlineIcon /> // Play icon in cirle
        case 'quiz':
            return <AssignmentIcon /> // a clipboard icon, used for take quiz
        case 'review':
            return <FindInPageIcon /> // a page with magnifying glass
        case 'requestcertificate':
            return <TurnedInIcon />
        case 'downloadCertificate':
            return <TurnedInIcon />
        default:
            return null;
    }
}

// creates the page header, with each header from the header list
// function SimpleTableHead(props) {
    
//     return (
//         <TableHead>
//             <TableRow>
//                 {props.actions &&
//                     <TableCell key="actions">
//                         Actions
//                     </TableCell>
//                 }
//                 {props.tableCells.map((tableCell) => (
//                     <TableCell key={tableCell.id} align={tableCell.centered ? "center" : "inherit"}>
//                         {tableCell.label}
//                     </TableCell>
//                 ))}
//             </TableRow>
//         </TableHead>
//     );
// }

// table actions with buttons invisible instead of disabled
/*
        <TableCell key="actions">
            {props.actionButtons.map((button) => 
                <span key={button.icon}>
                {!props.actionsDisabled[button.icon] && 
                <Tooltip title={button.label || button.icon[0].toUpperCase() + button.icon.substr(1)}>
                    <IconButton
                        data-tag={props.cellKey}
                        className={button.delete ? props.classes.deleteButton : ""}
                        onClick={() => button.action(props.item[button.attribute] || props.cellKey)}
                        // disabled={actionsDisabled && actionsDisabled[button.icon] || false}
                    >
                        {getIcon(button.icon)}
                    </IconButton>
                </Tooltip>}
                </span>
            )}
        </TableCell>

*/
function TableActions(props) {
    // let actionsDisabled = props.actionsDisabled || null;

    return (
        <TableCell key="actions" style={props.style}>
            <Grid container direction="row" alignItems="center" >
                {props.actionButtons.map((button) => (
                    <span key={button.icon || 'noshow'}  className='gridItemSpacing'>
                        {
                            // remove the button if the view attribute is defined and the action disabled prop for that is false
                            (!button.view || (button.view && props.actionsDisabled[button.view])) &&
                            <Button
                                variant="contained"
                                size="small"
                                data-tag={props.cellKey}
                                className={button.delete ? props.classes.deleteButton : ""}
                                onClick={() => button.action(props.item[button.attribute] || props.cellKey)}
                                // disabled={actionsDisabled && actionsDisabled[button.icon] || false}
                                disabled={props.actionsDisabled && props.actionsDisabled[button.icon]}
                                startIcon={getIcon(button.icon)}
                            >
                                {button.label ? button.label : button.icon}
                            </Button>}
                    </span>
                ))}
            </Grid>
        </TableCell>
    );
}

// added hyperlink support. Add a hyperlink prop to the table cell, (add relative: false if you do not want the id to be appended to the link)
export default function SimpleTable(props) {
    const classes = useStyles();
    const items = props.data;
    const tableCells = props.tableCells;
    const highlightCell = props.highlight || null
    // console.log(tableCells)
    


    return (
        <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="simple table">
                <SimpleTableHead
                    classes={classes}
                    tableCells={tableCells}
                    actions={props.actions}
                />
                <TableBody>
                    {items.map((row) => {
                        let cellKey = props.cellKey;

                        //the row highlighting
                        let backGround = {}
                        if (highlightCell && row[highlightCell] ) backGround = { backgroundColor: 'lightyellow' }

                        let cells = tableCells.map((cell) => {
                            let cellName = cell.id;
                            // console.log(cell)
                            return <TableCell key={cellName} align={cell.centered ? 'center' : 'inherit'} style={backGround}>
                                {cell.hyperlink ?
                                    // the react linter did not like anchor tags without an href, so I used there suggestion and did a button
                                    <button
                                        className='link-button'
                                        onClick={() => cell.hyperlink(row[cellKey])}
                                    >
                                        {row[cellName]}
                                    </button> :
                                    row[cellName]
                                }
                            </TableCell>
                        });

                        return (
                            <TableRow key={row[cellKey]} data-tag={row[cellKey]}>
                                {
                                    
                                props.actions &&
                                    <TableActions
                                        classes={classes}
                                        actionButtons={props.actionButtons}
                                        item={row}
                                        cellKey={row[cellKey]}
                                        actionsDisabled={row['actionsDisabled']}
                                        style={backGround}
                                    />
                                }
                                {cells}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export const MemoTable = React.memo(SimpleTable);
